import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSiteConfig, useAuth } from '../../hooks/useSupabase';
import { useStorage } from '../../hooks/useStorage';
import SafeIcon from '../../common/SafeIcon';
import ImageWithFallback from '../../components/ImageWithFallback';
import ImagePicker from '../../components/ImagePicker';
import supabase from '../../lib/supabase';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiUpload, FiAlertCircle, FiShield, FiHome, FiInfo, FiUsers, FiShare2 } = FiIcons;

const SiteSettings = () => {
  const { data: siteConfigData, loading: configLoading } = useSiteConfig();
  const { user } = useAuth();
  const { uploadFile, uploading, error: uploadError } = useStorage();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    logo_url: '',
    primary_color: '#1d4ed8',
    secondary_color: '#1e293b',
    site_name: 'Α.Ο. Χανιά',
    site_description: 'Γυναικεία Ομάδα Ποδοσφαίρου',
    social_facebook: 'https://www.facebook.com/aoxaniawfc',
    social_instagram: 'https://www.instagram.com/aoxaniawfc',
    social_youtube: 'https://www.youtube.com/@AOXaniawfc',
    phone: '+30 28210 12345',
    email: 'info@aoxania.gr',
    address: 'Δημοτικό Στάδιο Μοναχής Ελιάς, Χανιά',
    recaptcha_site_key: '',
    recaptcha_secret_key: '',
    contact_text: 'Επικοινωνήστε μαζί μας για οποιαδήποτε πληροφορία',
    // Home page statistics
    stats_founding_year: '1945',
    stats_players_count: '20+',
    stats_league_division: "Γ'",
    stats_years_history: '80',
    // Footer about text
    footer_about_text: 'Από το 1945, ο Α.Ο. Χανιά συνεχίζει την παράδοση στο ποδόσφαιρο. Η γυναικεία μας ομάδα αγωνίζεται στη Γ\' Εθνική Κατηγορία με στόχο την προώθηση του γυναικείου ποδοσφαίρου στην Κρήτη.',
    // Team page history text
    team_history_text: 'Ο Α.Ο. Χανιά ιδρύθηκε το 1945 και αποτελεί έναν από τους παλαιότερους συλλόγους της Κρήτης. Μέχρι το 2017, ο σύλλογος διατηρούσε και ανδρική ομάδα. Από τότε, συνεχίζει αποκλειστικά με τη γυναικεία του ομάδα, η οποία αγωνίζεται στη Γ\' Εθνική Κατηγορία. Η ομάδα έχει ως στόχο την προώθηση του γυναικείου ποδοσφαίρου στην περιοχή και την ανάδειξη νέων ταλέντων.',
    // Academy carousel visibility settings
    show_k17_carousel: true,
    show_k15_carousel: true,
    show_k13_carousel: true,
    // Academy registration banner image
    academy_banner_image: '',
    // Social Sharing Image
    social_share_image: '',
  });

  const [saving, setSaving] = useState(false);
  const [savingRecaptcha, setSavingRecaptcha] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [recaptchaMessage, setRecaptchaMessage] = useState({ type: '', text: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [recaptchaStatus, setRecaptchaStatus] = useState('unknown');
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (!configLoading && siteConfigData && siteConfigData.length > 0) {
      const config = siteConfigData[0];
      setFormData({
        ...config,
        recaptcha_site_key: config.recaptcha_site_key || '',
        recaptcha_secret_key: config.recaptcha_secret_key || '',
        contact_text: config.contact_text || 'Επικοινωνήστε μαζί μας για οποιαδήποτε πληροφορία',
        social_facebook: config.social_facebook || 'https://www.facebook.com/aoxaniawfc',
        social_instagram: config.social_instagram || 'https://www.instagram.com/aoxaniawfc',
        social_youtube: config.social_youtube || 'https://www.youtube.com/@AOXaniawfc',
        stats_founding_year: config.stats_founding_year || '1945',
        stats_players_count: config.stats_players_count || '20+',
        stats_league_division: config.stats_league_division || "Γ'",
        stats_years_history: config.stats_years_history || '80',
        footer_about_text: config.footer_about_text || 'Από το 1945, ο Α.Ο. Χανιά συνεχίζει την παράδοση στο ποδόσφαιρο. Η γυναικεία μας ομάδα αγωνίζεται στη Γ\' Εθνική Κατηγορία με στόχο την προώθηση του γυναικείου ποδοσφαίρου στην Κρήτη.',
        team_history_text: config.team_history_text || 'Ο Α.Ο. Χανιά ιδρύθηκε το 1945 και αποτελεί έναν από τους παλαιότερους συλλόγους της Κρήτης. Μέχρι το 2017, ο σύλλογος διατηρούσε και ανδρική ομάδα. Από τότε, συνεχίζει αποκλειστικά με τη γυναικεία του ομάδα, η οποία αγωνίζεται στη Γ\' Εθνική Κατηγορία. Η ομάδα έχει ως στόχο την προώθηση του γυναικείου ποδοσφαίρου στην περιοχή και την ανάδειξη νέων ταλέντων.',
        // Academy carousel visibility settings with defaults
        show_k17_carousel: config.show_k17_carousel !== false,
        show_k15_carousel: config.show_k15_carousel !== false,
        show_k13_carousel: config.show_k13_carousel !== false,
        // Academy banner image
        academy_banner_image: config.academy_banner_image || '',
        // Social Share Image
        social_share_image: config.social_share_image || '',
        // Address fallback
        address: config.address || 'Δημοτικό Στάδιο Μοναχής Ελιάς, Χανιά'
      });
    }
  }, [siteConfigData, configLoading]);

  // Check if recaptcha column exists
  useEffect(() => {
    const checkColumns = async () => {
      // Need to check if columns exist to show appropriate UI
      // We can infer this from whether the data loaded successfully
      if (siteConfigData && siteConfigData.length > 0) {
        setRecaptchaStatus('exists');
      }
    };
    checkColumns();
  }, [siteConfigData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleQuillChange = (value, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      if (!allowedTypes.includes(file.type)) {
        setMessage({ type: 'error', text: 'Παρακαλώ ανεβάστε μια έγκυρη εικόνα (JPG, PNG, GIF, WebP)' });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Το αρχείο είναι πολύ μεγάλο. Παρακαλώ ανεβάστε αρχείο μικρότερο από 5MB.' });
        return;
      }

      setLogoFile(file);
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        setPreviewUrl(event.target.result);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleAcademyBannerChange = (imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      academy_banner_image: imageUrl
    }));
  };

  const handleSocialShareImageChange = (imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      social_share_image: imageUrl
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      let logoUrl = formData.logo_url;

      // Upload logo if there's a new one
      if (logoFile) {
        console.log('Uploading new logo file...');
        const uploadedUrl = await uploadFile(logoFile, 'team_logos', 'logos/');
        if (uploadedUrl) {
          logoUrl = uploadedUrl;
          console.log('Logo uploaded successfully:', logoUrl);
        } else {
          throw new Error(uploadError || 'Failed to upload logo');
        }
      }

      const updatedData = {
        ...formData,
        logo_url: logoUrl,
        updated_at: new Date().toISOString(),
        // Keep existing reCAPTCHA keys
        recaptcha_site_key: siteConfigData[0].recaptcha_site_key || '',
        recaptcha_secret_key: siteConfigData[0].recaptcha_secret_key || ''
      };

      try {
        const { error } = await supabase
          .from('site_config_aox1234')
          .update(updatedData)
          .eq('id', formData.id);

        if (error) throw error;
        console.log('Site config updated successfully');
      } catch (dbError) {
        console.error('Database update failed:', dbError);
      }

      setFormData(updatedData);
      window.localStorage.setItem('aox_site_config', JSON.stringify(updatedData));
      setMessage({ type: 'success', text: 'Οι ρυθμίσεις αποθηκεύτηκαν επιτυχώς!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: `Σφάλμα: ${error.message || 'Προέκυψε πρόβλημα κατά την αποθήκευση'}` });
    } finally {
      setSaving(false);
      setLogoFile(null);
      setPreviewUrl('');
    }
  };

  const handleSaveRecaptcha = async (e) => {
    e.preventDefault();
    setSavingRecaptcha(true);
    setRecaptchaMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('site_config_aox1234')
        .update({
          recaptcha_site_key: formData.recaptcha_site_key,
          recaptcha_secret_key: formData.recaptcha_secret_key,
          updated_at: new Date().toISOString()
        })
        .eq('id', formData.id);

      if (error) throw error;
      setRecaptchaMessage({ type: 'success', text: 'Οι ρυθμίσεις reCAPTCHA αποθηκεύτηκαν!' });
    } catch (error) {
      console.error('Error saving recaptcha settings:', error);
      setRecaptchaMessage({ type: 'error', text: `Σφάλμα: ${error.message}` });
    } finally {
      setSavingRecaptcha(false);
    }
  };

  if (configLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Βασικές Πληροφορίες', icon: FiInfo },
    { id: 'appearance', label: 'Εμφάνιση', icon: FiHome },
    { id: 'content', label: 'Περιεχόμενο', icon: FiHome },
    { id: 'academy', label: 'Academy Carousels', icon: FiUsers },
    { id: 'security', label: 'Ασφάλεια', icon: FiShield }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Γενικές Ρυθμίσεις</h1>
            </div>
            <button
              onClick={handleSubmit}
              disabled={saving || uploading}
              className={`flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md ${(saving || uploading) ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
              {saving ? 'Αποθήκευση...' : uploading ? 'Μεταφόρτωση...' : 'Αποθήκευση'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-md flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
            {message.text}
          </motion.div>
        )}

        {uploadError && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 rounded-md p-4 text-sm flex items-center">
            <SafeIcon icon={FiAlertCircle} className="h-5 w-5 mr-2" />
            <span>Σφάλμα μεταφόρτωσης: {uploadError}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-6 font-medium text-sm transition-colors duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-b-2 border-primary-500 text-primary-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                }`}
              >
                <SafeIcon icon={tab.icon} className="w-5 h-5 inline mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Βασικές Πληροφορίες</h2>
              <form className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Logo Upload Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Λογότυπο
                    </label>
                    <div className="flex items-start space-x-4">
                      <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border">
                        <ImageWithFallback 
                          src={previewUrl || formData.logo_url} 
                          alt="Λογότυπο" 
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="cursor-pointer bg-white border border-gray-300 rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium text-gray-700 hover:bg-gray-50 mb-2">
                          <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                          {logoFile ? 'Αλλαγή αρχείου' : 'Επιλογή λογότυπου'}
                          <input type="file" className="sr-only" accept="image/*" onChange={handleLogoChange} />
                        </label>
                        {logoFile && (
                          <p className="text-sm text-green-600 mb-2">
                            Επιλέχθηκε: {logoFile.name}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          Προτεινόμενο μέγεθος: 512x512 pixels<br />
                          Μορφές: JPG, PNG, GIF, WebP (μέγιστο 5MB)
                        </p>
                        {uploading && (
                          <div className="mt-2">
                            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary-600 rounded-full transition-all duration-300"
                                style={{ width: '90%' }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Μεταφόρτωση...</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Social Share Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Εικόνα Social Share (Προεπιλογή)
                    </label>
                    <ImagePicker 
                      value={formData.social_share_image}
                      onChange={handleSocialShareImageChange}
                      folder="social"
                      bucket="team_logos"
                      aspectRatio="aspect-video"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Αυτή η εικόνα θα χρησιμοποιείται κατά την κοινοποίηση στα social media όταν δεν υπάρχει άλλη συγκεκριμένη εικόνα (π.χ. στην αρχική σελίδα).<br/>
                      Προτεινόμενο μέγεθος: 1200x630 pixels
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="site_name" className="block text-sm font-medium text-gray-700 mb-1">
                      Όνομα Συλλόγου
                    </label>
                    <input
                      type="text"
                      id="site_name"
                      name="site_name"
                      value={formData.site_name}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="site_description" className="block text-sm font-medium text-gray-700 mb-1">
                      Περιγραφή
                    </label>
                    <input
                      type="text"
                      id="site_description"
                      name="site_description"
                      value={formData.site_description}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Contact & Socials */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Επικοινωνία & Social Media</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Τηλέφωνο
                      </label>
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        Διεύθυνση
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_text" className="block text-sm font-medium text-gray-700 mb-1">
                        Κείμενο Επικοινωνίας
                      </label>
                      <input
                        type="text"
                        id="contact_text"
                        name="contact_text"
                        value={formData.contact_text}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Κείμενο που εμφανίζεται στη σελίδα επικοινωνίας"
                      />
                    </div>
                    <div>
                      <label htmlFor="social_facebook" className="block text-sm font-medium text-gray-700 mb-1">
                        Facebook URL
                      </label>
                      <input
                        type="url"
                        id="social_facebook"
                        name="social_facebook"
                        value={formData.social_facebook}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="social_instagram" className="block text-sm font-medium text-gray-700 mb-1">
                        Instagram URL
                      </label>
                      <input
                        type="url"
                        id="social_instagram"
                        name="social_instagram"
                        value={formData.social_instagram}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="social_youtube" className="block text-sm font-medium text-gray-700 mb-1">
                        YouTube URL
                      </label>
                      <input
                        type="url"
                        id="social_youtube"
                        name="social_youtube"
                        value={formData.social_youtube || ''}
                        onChange={handleInputChange}
                        placeholder="https://www.youtube.com/@AOXaniawfc"
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Χρησιμοποιείται στο footer και στα embedded YouTube videos της σελίδας Νέα & Media.
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Other Tabs (Appearance, Content, Academy, Security) remain unchanged... */}
          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Εμφάνιση</h2>
              <form className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Χρώματα</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="primary_color" className="block text-sm font-medium text-gray-700 mb-1">
                        Κύριο Χρώμα
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          id="primary_color"
                          name="primary_color"
                          value={formData.primary_color}
                          onChange={handleInputChange}
                          className="h-10 w-10 rounded border border-gray-300"
                        />
                        <input 
                          type="text" 
                          value={formData.primary_color}
                          onChange={handleInputChange}
                          name="primary_color"
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="secondary_color" className="block text-sm font-medium text-gray-700 mb-1">
                        Δευτερεύον Χρώμα
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          id="secondary_color"
                          name="secondary_color"
                          value={formData.secondary_color}
                          onChange={handleInputChange}
                          className="h-10 w-10 rounded border border-gray-300"
                        />
                        <input 
                          type="text" 
                          value={formData.secondary_color}
                          onChange={handleInputChange}
                          name="secondary_color"
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Περιεχόμενο Ιστοσελίδας</h2>
              <form className="space-y-8">
                {/* Home Page Statistics */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Στατιστικά Αρχικής Σελίδας</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                      <label htmlFor="stats_founding_year" className="block text-sm font-medium text-gray-700 mb-1">
                        Έτος Ίδρυσης
                      </label>
                      <input
                        type="text"
                        id="stats_founding_year"
                        name="stats_founding_year"
                        value={formData.stats_founding_year}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="stats_players_count" className="block text-sm font-medium text-gray-700 mb-1">
                        Αριθμός Παικτριών
                      </label>
                      <input
                        type="text"
                        id="stats_players_count"
                        name="stats_players_count"
                        value={formData.stats_players_count}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="stats_league_division" className="block text-sm font-medium text-gray-700 mb-1">
                        Κατηγορία Πρωταθλήματος
                      </label>
                      <input
                        type="text"
                        id="stats_league_division"
                        name="stats_league_division"
                        value={formData.stats_league_division}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="stats_years_history" className="block text-sm font-medium text-gray-700 mb-1">
                        Χρόνια Ιστορίας
                      </label>
                      <input
                        type="text"
                        id="stats_years_history"
                        name="stats_years_history"
                        value={formData.stats_years_history}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Footer About Text */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Κείμενο Υποσέλιδου</h3>
                  <div>
                    <label htmlFor="footer_about_text" className="block text-sm font-medium text-gray-700 mb-1">
                      Κείμενο Περιγραφής Συλλόγου
                    </label>
                    <textarea
                      id="footer_about_text"
                      name="footer_about_text"
                      rows="4"
                      value={formData.footer_about_text}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    ></textarea>
                  </div>
                </div>

                {/* Team Page History */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Ιστορία Ομάδας (Σελίδα "Η Ομάδα")</h3>
                  <div>
                    <label htmlFor="team_history_text" className="block text-sm font-medium text-gray-700 mb-1">
                      Κείμενο Ιστορίας
                    </label>
                    <ReactQuill 
                      theme="snow" 
                      value={formData.team_history_text} 
                      onChange={(value) => handleQuillChange(value, 'team_history_text')}
                      className="bg-white"
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['clean']
                        ]
                      }}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Μορφοποιήστε το κείμενο της ιστορίας της ομάδας που εμφανίζεται στη σελίδα "Η Ομάδα"
                    </p>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Academy Carousels Tab */}
          {activeTab === 'academy' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Academy Player Carousels</h2>
              <form className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Banner Academy</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Εικόνα Banner Academy
                    </label>
                    <ImagePicker 
                      value={formData.academy_banner_image} 
                      onChange={handleAcademyBannerChange}
                      folder="academy"
                      bucket="team_logos"
                      aspectRatio="aspect-video"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Προτεινόμενο μέγεθος: 600x400 pixels<br />
                      Εμφανίζεται στην αρχική σελίδα στο banner εγγραφής academy
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Ορατότητα Carousels</h3>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Κ17 Carousel</h3>
                      <p className="text-sm text-gray-600">Εμφάνιση carousel με παίκτριες της κατηγορίας Κ17</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="show_k17_carousel" 
                        checked={formData.show_k17_carousel} 
                        onChange={handleInputChange} 
                        className="sr-only" 
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${formData.show_k17_carousel ? 'bg-primary-600' : 'bg-gray-300'}`}>
                        <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${formData.show_k17_carousel ? 'translate-x-6' : 'translate-x-1'} mt-1`}></div>
                      </div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Κ14 Carousel</h3>
                      <p className="text-sm text-gray-600">Εμφάνιση carousel με παίκτριες της κατηγορίας Κ14</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="show_k15_carousel" 
                        checked={formData.show_k15_carousel} 
                        onChange={handleInputChange} 
                        className="sr-only" 
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${formData.show_k15_carousel ? 'bg-primary-600' : 'bg-gray-300'}`}>
                        <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${formData.show_k15_carousel ? 'translate-x-6' : 'translate-x-1'} mt-1`}></div>
                      </div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Κ12 Carousel</h3>
                      <p className="text-sm text-gray-600">Εμφάνιση carousel με παίκτριες της κατηγορίας Κ12</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="show_k13_carousel" 
                        checked={formData.show_k13_carousel} 
                        onChange={handleInputChange} 
                        className="sr-only" 
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${formData.show_k13_carousel ? 'bg-primary-600' : 'bg-gray-300'}`}>
                        <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${formData.show_k13_carousel ? 'translate-x-6' : 'translate-x-1'} mt-1`}></div>
                      </div>
                    </label>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center space-x-2 mb-6">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">reCAPTCHA Settings</h2>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Πώς να ρυθμίσετε το reCAPTCHA:</strong><br />
                  1. Πηγαίνετε στο <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="underline">Google reCAPTCHA Admin</a><br />
                  2. Δημιουργήστε ένα νέο site με τύπο "reCAPTCHA v3"<br />
                  3. Προσθέστε τον domain σας<br />
                  4. Αντιγράψτε τα κλειδιά εδώ
                </p>
              </div>

              {recaptchaMessage.text && (
                <div className={`mb-6 p-4 rounded-md flex items-center ${recaptchaMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
                  {recaptchaMessage.text}
                </div>
              )}

              <form onSubmit={handleSaveRecaptcha} className="space-y-6">
                <div>
                  <label htmlFor="recaptcha_site_key" className="block text-sm font-medium text-gray-700 mb-1">
                    Site Key
                  </label>
                  <input
                    type="text"
                    id="recaptcha_site_key"
                    name="recaptcha_site_key"
                    value={formData.recaptcha_site_key}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-mono"
                    placeholder="e.g. 6Lc..."
                  />
                </div>
                <div>
                  <label htmlFor="recaptcha_secret_key" className="block text-sm font-medium text-gray-700 mb-1">
                    Secret Key
                  </label>
                  <input
                    type="password"
                    id="recaptcha_secret_key"
                    name="recaptcha_secret_key"
                    value={formData.recaptcha_secret_key}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-mono"
                    placeholder="e.g. 6Lc..."
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingRecaptcha}
                    className={`flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md ${savingRecaptcha ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
                    {savingRecaptcha ? 'Αποθήκευση...' : 'Αποθήκευση reCAPTCHA'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteSettings;